import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { WebCompany, WebCustomer, WebRecentActivity } from "src/app/models";
import { Observable, Subscription } from "rxjs";
import { CompanyService, ContactService, CustomerService, SharedService } from "src/app/services";
import { HistoryService } from "src/app/services/history.service";
import { MatDialog } from "@angular/material/dialog";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { Router } from "@angular/router";
import { ContactListDialogComponent } from "../account-details/contact-list-dialog/contact-list-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SessionDataService } from "src/app/services/session-data.service";
import { AuthService } from "src/app/services";
import { filter, switchMap } from "rxjs/operators";
import { SummaryHeaderComponent } from "src/app/shared/summary-header/summary-header.component";
@Component({
  selector: "app-summary",
  templateUrl: "./summary.component.html",
  styleUrls: ["./summary.component.scss"],
})

export class SummaryComponent implements OnInit, OnDestroy
{
  private _summaryHeaderComponent?: SummaryHeaderComponent;

  @ViewChild(SummaryHeaderComponent)
  set summaryHeaderComponent(v: SummaryHeaderComponent | undefined)
  {
    this._summaryHeaderComponent = v;
    this.tryOpenExternalDeliveryDialog(); 
  }

  title: string = "Summary";
  private pendingExternalDeliveryDialog = false;
  private webSettingsReady = false;
  isEmployee: boolean = false;
  isAccountAccessRestricted: boolean = false;

  showActivities: boolean;

  branchCustId: string;
  announcement: string;
  showAnnouncement: boolean = false;

  customer: WebCustomer;
  customerSubscription: Subscription;
  webSettingsSubscription: Subscription;

  company: WebCompany;
  companySubscription: Subscription;

  authSubscription: Subscription;

  activities: WebRecentActivity[];

  contactsSubscription: Subscription;
  hasAlertShown: boolean;
  isDataReady$!: Observable<boolean>;

  constructor(
    private customerService: CustomerService,
    private companyService: CompanyService,
    private historyService: HistoryService,
    private authService: AuthService,
    public dialog: MatDialog,
    private contactService: ContactService,
    private sessionDataService: SessionDataService,
    public router: Router,
    public snackBar: MatSnackBar,
    public sharedService: SharedService
  ) { }

  ngOnInit(): void
  {
    this.webSettingsSubscription = this.companyService.webSettings.subscribe(webSettings =>
    {
      if (webSettings !== null)
      {
        this.webSettingsReady = true;
        this.checkAnnouncement(webSettings.Announcement);
        this.tryOpenExternalDeliveryDialog();
      }
    });

    this.authSubscription = this.authService.currentUserInfo.subscribe(
      (c) =>
      {
        this.isEmployee = c.isCompany;
      }
    );

    this.customerSubscription = this.customerService.customer.pipe(switchMap(customer =>
    {
      this.customer = customer;
      this.isAccountAccessRestricted =
        customer.AcctStat === 8 ||
        customer.AcctStat === 9 ||
        customer.AcctStatSameAsLegal;

      this.sessionDataService.CustId = customer.CustId;

      return this.companyService.company;
    })
    ).subscribe(company =>
    {
      this.company = company;
      this.showActivities = company.WebDisplayTransactions || this.sessionDataService.IsEmployee;

      this.historyService.recentActivities.subscribe(h =>
      {
        this.activities = h;
      });

      this.restrictAccess();
      this.externalDialogDelivery();
    });

    this.isDataReady$ = this.customerService.isDataReady$;

    this.contactsSubscription = this.contactService.contacts.subscribe(contacts =>
    {
      if (!contacts.some(d => d.Email))
      {
        if (this.customer && !this.isEmployee)
        {
          this.routeUser();
        }
      }
    });
  }

  private externalDialogDelivery(): void
  {
    if (!this.sharedService.isExternalLoginInstance) return;

    this.pendingExternalDeliveryDialog = true;

    this.tryOpenExternalDeliveryDialog();
  }

  private tryOpenExternalDeliveryDialog(): void
  {
    if (!this.sharedService.isExternalLoginInstance) return;
    if (!this.pendingExternalDeliveryDialog) return;
    if (!this.webSettingsReady) return;
    if (!this._summaryHeaderComponent) return;

    this._summaryHeaderComponent.openDialogDelivery();

    this.pendingExternalDeliveryDialog = false;
    this.sharedService.isExternalLoginInstance = false;
  }

  ngOnDestroy(): void
  {
    this.customerSubscription?.unsubscribe();
    this.webSettingsSubscription?.unsubscribe();
    this.contactsSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  checkAnnouncement(announcement: string): void
  {
    const html = (announcement ?? '').trim();
    this.showAnnouncement = false;
    this.announcement = html;

    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const hasImage = !!doc.querySelector('img');

    const text = (doc.body?.textContent ?? '')
      .replace(/\u00a0/g, ' ')                
      .replace(/[\u200B-\u200D\uFEFF]/g, '')   
      .trim();

    const hasText = text.length > 0;

    this.showAnnouncement = hasImage || hasText;
  }

  async routeUser(): Promise<void>
  {
    this.dialog.closeAll();
    await new Promise(r => setTimeout(r, 1000))
    if (this.contactService.checkEmails)
    {
      this.dialog.open(ContactListDialogComponent, {
        disableClose: true,
        width: "600px",
        maxHeight: "90%",
        maxWidth: "90%",
      });
    }
  }

  private restrictAccess()
  {
    if (this.hasAlertShown)
      return;
    if (SharedService.IsNullOrEmpty(this.sessionDataService.CustId))
    {
      this.hasAlertShown = true;
      this.dialog
        .open(AlertDialogComponent, {
          data: {
            title: "Alert",
            message: "There was an error retrieving your account information. \n Please try logging in again. \n If this problem persists please contact " +
              this.company.Name +
              ".\n Phone: " +
              this.company.Phone
          }
        })
        .afterClosed()
        .subscribe((_) =>
        {
          this.dialog.closeAll();
          this.authService.logout();
          this.customerService.clearCustomerData();
        });
    }
    if (!this.isEmployee) 
    {
      if (this.isAccountAccessRestricted)
      {
        this.hasAlertShown = true;
        this.dialog
          .open(AlertDialogComponent, {
            data: {
              title: "Warning",
              message:
                "Your account status does not allow you to log in to this website. \n Please contact " +
                this.company.Name +
                " \n Phone: " +
                this.company.Phone,
              buttonText: "Close",
            },
          })
          .afterClosed()
          .subscribe((_) =>
          {
            this.dialog.closeAll();
            this.authService.logout();
            this.customerService.clearCustomerData();
          });
      }
    }
  }
}